import {OptionButtonGroup} from 'src/design/atoms/OptionButtonGroup';
import {Switch} from 'src/design/atoms/Switch';
import {renderFieldLabel} from 'src/app/forms/renderFieldLabel';
import {toOptionButtonGroupColor} from 'src/app/forms/optionColors';
import React, {useCallback, useEffect, useMemo} from 'react';
import type {IFormFieldV2} from 'src/types/FormV2';

const POSITIVE_VALUE = 'true';
const NEGATIVE_VALUE = 'false';

export interface FormSwitchV2Props {
  field: IFormFieldV2;
  value?: boolean | string;
  error?: string;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

export const FormSwitchV2 = React.memo<FormSwitchV2Props>(
  ({field, value, error, onChange}: FormSwitchV2Props) => {
    const positiveText = useMemo(
      () => field.switchOptions?.positiveText ?? 'Yes',
      [field.switchOptions?.positiveText]
    );
    const negativeText = useMemo(
      () => field.switchOptions?.negativeText ?? 'No',
      [field.switchOptions?.negativeText]
    );

    const checked = useMemo(() => {
      if (typeof value === 'boolean') {
        return value;
      }
      return value === 'true' || value === '1';
    }, [value]);

    // Initialize undefined values to false
    useEffect(() => {
      if (value === undefined || value === null) {
        onChange(field.name, false, negativeText);
      }
    }, [field.name, value]);

    const handleOptionButtonChange = useCallback(
      (val: string | null) => {
        const newValue = val === POSITIVE_VALUE;
        onChange(field.name, newValue, newValue ? positiveText : negativeText);
      },
      [field.name, positiveText, negativeText, onChange]
    );

    const handleToggleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.currentTarget.checked;
        onChange(field.name, newValue, newValue ? positiveText : negativeText);
      },
      [field.name, positiveText, negativeText, onChange]
    );

    if (field.switchOptions?.control === 'toggle') {
      return (
        <Switch
          label={renderFieldLabel(field)}
          description={field.description}
          error={error}
          checked={checked}
          onChange={handleToggleChange}
          disabled={field.disabled}
          required={field.required}
        />
      );
    }

    return (
      <OptionButtonGroup
        label={renderFieldLabel(field)}
        ariaLabel={field.label}
        description={field.description}
        error={error}
        options={[
          {
            value: POSITIVE_VALUE,
            label: positiveText,
            color: toOptionButtonGroupColor(field.switchOptions?.positiveColor),
          },
          {
            value: NEGATIVE_VALUE,
            label: negativeText,
            color: toOptionButtonGroupColor(field.switchOptions?.negativeColor),
          },
        ]}
        value={checked ? POSITIVE_VALUE : NEGATIVE_VALUE}
        onChange={handleOptionButtonChange}
        allowDeselect={false}
        disabled={field.disabled}
      />
    );
  }
);
